/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  effect,
  Injector,
  OnInit,
  inject,
  runInInjectionContext,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {AbstractFormWrapper} from '../../../../../models/battery/abstract-form-wrapper';
import {ConnectorService} from '../../../../../services/connector.service';
import {UserService} from '../../../../../services/user.service';
import {
  FormFieldAttribute,
  FormPurpose,
} from '../../../../../models/FormCategory';
import {BatteryPassAttributeLabel} from '../../../../../models/BatteryPassAttributeLabel';
import {
  ApiAssignmentPatchDto,
  AssignmentPatchRequestDto,
} from '../../../../../models/battery/AssignmentPatchDto';
import {GeneralDataHandlingService} from '../../../../../services/general.data-handling.service';
import {UmsOrganization} from '../../../../../models/user-management/UserDto';
import {PassportDto} from '../../../../../models/Passport';

const ASSIGNMENT_SUBMISSION_DELAY_MS = 4000;

@Component({
  selector: 'app-data-provider-assignment-details',
  templateUrl: '../../edit-battery-pass/edit-battery-pass.component.html',
  styleUrl: '../../edit-battery-pass/edit-battery-pass.component.scss',
  standalone: false,
})
export class DataProviderAssignmentDetailsComponent
  extends AbstractFormWrapper
  implements OnInit
{
  // Additional services specific to this component
  readonly generalDataHandlingService = inject(GeneralDataHandlingService);

  override purpose: FormPurpose = 'assignment';
  openAttributes: BatteryPassAttributeLabel[] = [];
  filledAttributeList: ApiAssignmentPatchDto[] = [];

  override setupEffect() {
    // Call parent setupEffect first
    super.setupEffect();

    // Add component-specific effect
    runInInjectionContext(this.injector, () => {
      effect(async () => {
        const passport = this.passportService.passes()[this.passportId];
        if (passport) {
          const schemaDto = await this.passportService.loadSchema(
            passport.metadata?.schemaId ?? '',
          );
          this.schema = schemaDto.data;
          this.schemaName = schemaDto.data.name;
          this.passDisplayName = passport?.metadata?.name
            ? `${passport.metadata.name}`
            : this.passportId;
        }
        this.attributeSelections =
          this.assignmentService.attributeSelections()[this.passportId] || [];
        const userList = this.userManagementService.userList();
        if (userList) {
          this.dataProviders =
            await this.userManagementService.getDataProviderList();
          this.isLoadingUserData = false;
        }
      });
    });
  }

  override async afterNgOnInit() {
    this.loading = true;
    await this.setOptions();
    await this.checkForEdc();

    this.cxAssetForm = this.createCxAssetForm();
    this.openAttributes = await this.loadOpenAttributes();
    await this.passportService.loadOnePublic(this.passportId);
    this.loading = false;
  }

  async setOptions() {
    const featureFlag = await this.featureFlaggingService.load();
    if (
      featureFlag?.features.supplierApi === true &&
      featureFlag?.features.cx === true
    ) {
      this.canSwitchProvisionType = true;
      this.isCatenaXEnabled = false;
    } else if (
      featureFlag?.features.supplierApi === false &&
      featureFlag?.features.cx === true
    ) {
      this.canSwitchProvisionType = false;
      this.isCatenaXEnabled = true;
    } else {
      this.canSwitchProvisionType = false;
      this.isCatenaXEnabled = false;
    }
  }

  createCxAssetForm(): FormGroup {
    return new FormGroup({
      assetId: new FormControl(null, {
        validators: [
          Validators.required,
          Validators.pattern(
            /^CX:([0-9a-zA-Z-_]{8,36}):([0-9a-zA-Z-_]{8,36})$/,
          ),
        ],
      }),
      schema: new FormControl('CX_BP', {validators: [Validators.required]}),
    });
  }

  async loadOpenAttributes(): Promise<BatteryPassAttributeLabel[]> {
    return new Promise(async (resolve) => {
      this.userService.userDataChange.subscribe(async (user) => {
        const attributes =
          await this.assignmentService.loadAttributeAssignments(
            user?.id,
            this.passportId,
          );
        resolve(
          attributes.filter((assignment) => assignment.status !== 'complete'),
        );
      });
    });
  }

  /**
   * This function loads all organizations a user have
   * Then it checks if one of these org has the role SYS_ADMIN
   * If yes, then it will check if there is one connector configured. If yes,
   * it will take this one as given and will not check the other organizations
   */
  async checkForEdc() {
    this.edcConfigured = false;
    const user = await this.userService.loadRawUser();
    const organizations: UmsOrganization[] = user?.organizations;

    if (
      !organizations
        .map((organization) =>
          organization.roles?.flatMap((role) => role.label),
        )
        .some(
          (roleLabels) =>
            roleLabels?.includes('DATA_PROVIDER') ||
            roleLabels?.includes('SYS_ADMIN'),
        )
    ) {
      return;
    }

    const connectors = await this.connectorService.load(
      organizations[0].id,
      'provider',
      true,
    );
    this.edcConfigured = connectors.length > 0;
    if (!this.edcConfigured && this.isCatenaXEnabled) {
      const failureMessage = await this.translate.instant(
        'battery_pass.edit.errors.edc_not_configured',
      );
      await this.toastService.show({
        message: failureMessage,
        autoClose: true,
        type: 'warning',
      });
    }
  }

  override async onSaveClick(_status: 'complete' | 'draft'): Promise<void> {
    if (this.isCatenaXEnabled) {
      await this.onSubmitCx();
    } else {
      await this.onSubmitApi();
    }
  }

  async onSubmitApi() {
    this.filledAttributeList = this.buildFilledAttributeList();
    this.passportService
      .saveAttributeAssignments(
        {data: this.filledAttributeList},
        'API',
        false,
        true,
      )
      .then(async () => {
        this.loading = true;
        // Wait for 4 seconds to ensure backend processing is complete and data consistency is achieved
        // before navigating away or closing the dialog. This helps prevent race conditions or incomplete updates.
        setTimeout(async () => {
          await this.closeSuccessful();
        }, ASSIGNMENT_SUBMISSION_DELAY_MS);
      })
      .catch(async (error) => {
        await this.toastService.show({
          message: await this.translate.instant(
            'battery_pass.edit.attribute_assignments_pane.errors.submission',
            {error: error.error?.message || error.message},
          ),
          type: 'error',
        });
      });
  }

  override async onSubmitCx() {
    this.filledAttributeList = this.buildFilledAttributeList();
    this.passportService
      .saveAttributeAssignments(
        {data: this.filledAttributeList},
        'CX',
        false,
        true,
      )
      .then(async () => {
        await this.closeSuccessful();
      })
      .catch(async (error) => {
        await this.toastService.show({
          message: await this.translate.instant(
            'battery_pass.edit.attribute_assignments_pane.errors.submission',
            {error: error.error?.message || error.message},
          ),
          type: 'error',
        });
      });
  }

  override async onSaveImport(): Promise<void> {
    const isExistingAsset: boolean = this.cxAssetForm.valid;
    if (isExistingAsset) {
      const {assetId, schema} = this.cxAssetForm.value;
      const submissionDto = {
        data: this.openAttributes.map((attribute) => {
          return {id: attribute.id, assetId, schema};
        }),
      };
      await this.passportService.saveAttributeAssignments(
        submissionDto as unknown as AssignmentPatchRequestDto,
        'CX',
        true,
      );
    }
    await this.closeSuccessful();
  }

  override onProvisionTypeCXClick() {
    this.isCatenaXEnabled = true;
  }

  override onProvisionTypeAPIClick() {
    this.isCatenaXEnabled = false;
  }

  override async onProvideAssetClick() {
    const instance = await this.modalService.open({
      content: this.provideAssetModalRef,
      centered: true,
    });

    instance.onClose.on(async (shouldCancel) => {
      if (shouldCancel) {
        await this.router.navigate(['/app/assign-attributes']);
      }
    });
  }

  async closeSuccessful() {
    const successMessage = await this.translate.instant(
      'battery_pass.attribute_selection.save_success',
    );
    await this.toastService.show({
      message: successMessage,
      type: 'success',
    });
    await this.router.navigate(['/app/assign-attributes']);
  }

  /**
   * Builds the attribute-assignment submission payload directly from the
   * current `passport.data` (the single source of truth for form values)
   * instead of an incrementally-accumulated side list.
   *
   * Previously `filledAttributeList` was built up one field-change-event at a
   * time inside `saveFunction()`. That meant an open attribute was only
   * included in the submission if it had been *actively edited in the current
   * session* — fields that were already correctly filled (e.g. from a
   * previous save, or simply not touched again after reopening a composite
   * field like the address modal) were silently dropped from the payload,
   * even though they were visibly present in the form. Deriving the payload
   * fresh from `passport.data` for every open attribute guarantees the
   * submission always reflects the complete, currently visible form state.
   */
  buildFilledAttributeList(): ApiAssignmentPatchDto[] {
    const passport = this.passportService.passes()[this.passportId];
    const passportData = passport?.data ?? {};
    return this.openAttributes
      .filter((attribute) => !!attribute.id && !!attribute.attribute)
      .map((attribute) => ({
        id: attribute.id as string,
        value: this.generalDataHandlingService.getValueByPath(
          passportData,
          attribute.attribute,
        ),
      }))
      .filter((entry) => entry.value !== undefined && entry.value !== null);
  }

  override saveFunction(
    value: string | number,
    _attribute: FormFieldAttribute,
    _control: AbstractControl,
    fullValuePath: string,
  ) {
    // Store data in the passport structure for address fields. This is the
    // single source of truth: the submission payload is derived from it fresh
    // at submit time via `buildFilledAttributeList()`, rather than mirrored
    // incrementally here.
    const passes = this.passportService.passes();
    const passport = passes[this.passportId] ?? {
      data: {},
    };
    this.generalDataHandlingService.setValueByPath(
      passport.data,
      fullValuePath,
      value,
    );
    passes[this.passportId] = passport as PassportDto;
    this.passportService.passes.set(passes);
  }
}
